/**
 * MonthlyCalendar — Training calendar for the History page
 *
 * Shows the current month with dots on trained days.
 * Tapping a trained day filters the session list to that date.
 * Prev/next month navigation included.
 */

import { useLiveQuery } from 'dexie-react-hooks';
import { useState } from 'react';
import { db } from '@/lib/db';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const SPLIT_COLORS: Record<string, string> = {
  push:          'bg-accent',
  anterior:      'bg-accent',
  pull:          'bg-success',
  posterior:     'bg-success',
  legs:          'bg-warning',
  upper:         'bg-accent',
  lower:         'bg-success',
  full_body_day: 'bg-accent',
};

interface Props {
  onDaySelect: (dateKey: string | null) => void;
  selectedDay: string | null;
}

export default function MonthlyCalendar({ onDaySelect, selectedDay }: Props) {
  const today = new Date();
  const [viewYear, setViewYear]   = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());

  const monthStart = new Date(viewYear, viewMonth, 1);
  const monthEnd   = new Date(viewYear, viewMonth + 1, 0);

  const sessions = useLiveQuery(async () => {
    const start = new Date(viewYear, viewMonth, 1).toISOString();
    const end   = new Date(viewYear, viewMonth + 1, 0, 23, 59, 59).toISOString();
    return db.workouts
      .where('startedAt').between(start, end)
      .filter((w) => w.status === 'completed')
      .toArray();
  }, [viewYear, viewMonth]) ?? [];

  // Map date-key → splitDay for colouring
  const byDay = new Map<string, string>();
  for (const s of sessions) {
    byDay.set(s.startedAt.slice(0, 10), s.splitDay);
  }

  // Build grid: pad start with empty cells
  const startDow = monthStart.getDay(); // 0=Sun
  const totalDays = monthEnd.getDate();
  const cells: (number | null)[] = [
    ...Array(startDow).fill(null),
    ...Array.from({ length: totalDays }, (_, i) => i + 1),
  ];
  // Pad to complete the last week
  while (cells.length % 7 !== 0) cells.push(null);

  const prevMonth = () => {
    if (viewMonth === 0) { setViewYear((y) => y - 1); setViewMonth(11); }
    else setViewMonth((m) => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewYear((y) => y + 1); setViewMonth(0); }
    else setViewMonth((m) => m + 1);
  };

  const monthLabel = new Date(viewYear, viewMonth, 1)
    .toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  return (
    <div className="card space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={prevMonth}
          className="w-8 h-8 rounded-xl bg-surface-300 flex items-center justify-center
                     text-ink-muted hover:bg-surface-400 active:scale-90 transition-all"
        >‹</button>
        <p className="font-semibold text-ink text-sm">{monthLabel}</p>
        <button
          onClick={nextMonth}
          className="w-8 h-8 rounded-xl bg-surface-300 flex items-center justify-center
                     text-ink-muted hover:bg-surface-400 active:scale-90 transition-all"
        >›</button>
      </div>

      {/* Weekday headers */}
      <div className="grid grid-cols-7 gap-1">
        {WEEKDAYS.map((d) => (
          <p key={d} className="text-center text-xs text-ink-faint font-medium">{d}</p>
        ))}
      </div>

      {/* Days grid */}
      <div className="grid grid-cols-7 gap-1">
        {cells.map((day, idx) => {
          if (day === null) return <div key={`empty-${idx}`} />;
          const dateKey = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const isToday = dateKey === today.toISOString().slice(0, 10);
          const splitDay = byDay.get(dateKey);
          const trained = !!splitDay;
          const isSelected = selectedDay === dateKey;
          const dotColor = SPLIT_COLORS[splitDay ?? ''] ?? 'bg-accent';

          return (
            <button
              key={dateKey}
              onClick={() => onDaySelect(isSelected ? null : (trained ? dateKey : null))}
              disabled={!trained}
              className={`flex flex-col items-center py-1.5 rounded-xl transition-all
                          ${isSelected ? 'bg-accent/20 ring-1 ring-accent' : ''}
                          ${trained && !isSelected ? 'hover:bg-surface-300 active:scale-95' : ''}
                          ${isToday ? 'font-bold' : ''}`}
            >
              <span className={`text-xs ${
                isSelected ? 'text-accent' :
                isToday ? 'text-accent' :
                trained ? 'text-ink' : 'text-ink-faint'
              }`}>
                {day}
              </span>
              {trained && (
                <span className={`w-1.5 h-1.5 rounded-full mt-0.5 ${dotColor}`} />
              )}
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex gap-3 justify-center pt-1">
        {[
          { color: 'bg-accent',  label: 'Push / Upper' },
          { color: 'bg-success', label: 'Pull / Lower' },
          { color: 'bg-warning', label: 'Legs' },
        ].map(({ color, label }) => (
          <div key={label} className="flex items-center gap-1">
            <span className={`w-2 h-2 rounded-full ${color}`} />
            <span className="text-xs text-ink-faint">{label}</span>
          </div>
        ))}
      </div>

      {selectedDay && (
        <button
          onClick={() => onDaySelect(null)}
          className="w-full text-xs text-accent text-center py-1 hover:text-accent/80"
        >
          Clear filter ✕
        </button>
      )}
    </div>
  );
}
